import React, { useCallback, useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "tailwindcss/tailwind.css";
import { BASE_URL } from "../../config";
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilSquareIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  FlagIcon
} from "@heroicons/react/24/outline";
import { useTable, usePagination } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faFileMedical, faFileUpload, faPlus } from "@fortawesome/free-solid-svg-icons";
import clsx from "clsx";

const ACTION_MENU_WIDTH = 288;
const ACTION_MENU_HEIGHT = 320;
const ACTION_MENU_MARGIN = 12;
const ACTION_MENU_GAP = 8;

const getActionMenuPosition = (triggerElement) => {
  const rect = triggerElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = rect.right - ACTION_MENU_WIDTH;
  left = Math.max(
    ACTION_MENU_MARGIN,
    Math.min(left, viewportWidth - ACTION_MENU_WIDTH - ACTION_MENU_MARGIN)
  );

  let top = rect.bottom + ACTION_MENU_GAP;
  let placement = "bottom";

  if (top + ACTION_MENU_HEIGHT > viewportHeight - ACTION_MENU_MARGIN) {
    top = rect.top - ACTION_MENU_HEIGHT - ACTION_MENU_GAP;
    placement = "top";
  }

  const maxTop = Math.max(
    ACTION_MENU_MARGIN,
    viewportHeight - ACTION_MENU_HEIGHT - ACTION_MENU_MARGIN
  );
  top = Math.max(ACTION_MENU_MARGIN, Math.min(top, maxTop));

  return { top, left, placement };
};

const getRowActionKey = (cpt) => String(cpt?.cpt_code || "");

const Badge = ({ children, color = "gray" }) => {
  const colors = {
    gray: "bg-gray-100 text-gray-700 ring-gray-200",
    green: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    blue: "bg-blue-100 text-blue-700 ring-blue-200",
    red: "bg-rose-100 text-rose-700 ring-rose-200",
    yellow: "bg-amber-100 text-amber-700 ring-amber-200",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <span
      className={clsx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1",
        colors[color]
      )}
    >
      {children}
    </span>
  );
};

const EmptyState = ({
  title = "No results",
  subtitle = "Try adjusting your search or filters.",
}) => (
  <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 p-10 text-center">
    <svg
      viewBox="0 0 24 24"
      className="mb-2 h-8 w-8 text-gray-400"
      fill="none"
      stroke="currentColor"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
    <div className="text-sm font-medium text-gray-900">{title}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

const LoadingModal = ({ isOpen }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="rounded-2xl bg-white p-8 shadow-2xl flex flex-col items-center gap-4"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeInOut" }}
        >
          {/* Modern Loader */}
          <motion.div
            className="relative h-12 w-12"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.2, ease: "linear" }}
          >
            <div className="absolute inset-0 rounded-full border-4 border-t-blue-600 border-r-transparent border-b-transparent border-l-transparent"></div>
            <div className="absolute inset-2 rounded-full border-4 border-t-transparent border-r-blue-400 border-b-transparent border-l-transparent opacity-70"></div>
          </motion.div>

          {/* Text */}
          <motion.span
            className="text-base font-semibold text-gray-700 text-center"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            Generating your file...
          </motion.span>
          <span className="text-sm text-gray-500 animate-pulse">
            Please wait a moment
          </span>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const CptTable = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("cpt_code");
  const [sortDirection, setSortDirection] = useState("asc");

  const [statusFilter, setStatusFilter] = useState("ALL");

  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedCpt, setSelectedCpt] = useState(null);
  const [newStatus, setNewStatus] = useState("2"); // default Terminated/Cancelled
  const [remarks, setRemarks] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const fetchCpts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/cpt/get-cpt-codes-v2`, {
        headers: getAuthHeaders(),
        params: {
          limit_offset: (currentPage - 1) * itemsPerPage,
          search_string: searchQuery,
          sort_by: sortBy,
          sort_direction: sortDirection,
          limit: itemsPerPage,
          status_flag: statusFilter === "ALL" ? undefined : statusFilter,
        },
      });
      setData(response.data.data.cpts || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error("Failed to fetch CPT codes.", { autoClose: 3000 });
      setTimeout(() => navigate("/"), 2000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCpts();
  }, [currentPage, itemsPerPage, searchQuery, sortBy, sortDirection, statusFilter]);

  const statusMap = {
    1: { name: "Active", color: "green" },
    2: { name: "Terminated/Cancelled", color: "red" },
    3: { name: "Suspended", color: "yellow" },
    default: { name: "Unknown", color: "gray" },
  };

  const statusOptions = [
    { value: "1", label: "Active" },
    { value: "2", label: "Terminated/Cancelled" },
    { value: "3", label: "Suspended" },
  ];

  const getStatusValue = (activeFlag) =>
    activeFlag === null || activeFlag === undefined ? "" : String(activeFlag);

  const getStatusLabel = (activeFlag) => {
    const status = statusMap[getStatusValue(activeFlag)] || statusMap.default;
    return status.name;
  };

  const closeActionMenu = useCallback(() => {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
  }, []);

  const handleActionMenuToggle = useCallback(
    (cpt, event) => {
      event.stopPropagation();
      const rowKey = getRowActionKey(cpt);

      if (openActionMenu === rowKey) {
        closeActionMenu();
        return;
      }

      setOpenActionMenu(rowKey);
      setActionMenuPosition(getActionMenuPosition(event.currentTarget));
    },
    [closeActionMenu, openActionMenu]
  );

  useEffect(() => {
    if (!openActionMenu) {
      return undefined;
    }

    const handleViewportChange = () => closeActionMenu();
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [closeActionMenu, openActionMenu]);

  const getRowActions = (cpt) => [
    {
      key: "view",
      label: "View CPT details",
      Icon: EyeIcon,
      color: "text-blue-600",
      onClick: () => navigate(`/cpt/view/${cpt.cpt_code}`),
    },
    {
      key: "edit",
      label: "Edit CPT details",
      Icon: PencilSquareIcon,
      color: "text-amber-600",
      onClick: () => navigate(`/cpt/edit/${cpt.cpt_code}`),
    },
    {
      key: "history",
      label: "View trail log history",
      Icon: ClockIcon,
      color: "text-purple-600",
      onClick: () => navigate(`/cpt/history/${cpt.cpt_code}`),
    },
    {
      key: "status",
      label: "Set CPT status",
      Icon: FlagIcon,
      color: "text-indigo-600",
      onClick: () => openStatusModal(cpt),
    },
  ];

  const columns = useMemo(
    () => [
      {
        Header: "CPT Code",
        accessor: "cpt_code",
        Cell: ({ value }) => <span className="font-medium text-gray-900">{value || "N/A"}</span>,
      },
      {
        Header: "Description",
        accessor: "cpt_desc",
        Cell: ({ value }) => <span className="text-gray-600">{value || "N/A"}</span>,
      },
      {
        Header: "Total CPT Rates Count",
        accessor: "total_rates_count",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Category",
        accessor: "cpt_categ_code",
        Cell: ({ value }) => <span className="text-gray-700">{value || "N/A"}</span>,
      },
      {
        Header: "Default Amount",
        accessor: "default_price",
        Cell: ({ value }) => <span className="text-gray-700">₱{parseFloat(value || 0).toLocaleString()}</span>,
      },
      {
        Header: "Status",
        accessor: "active_flag",
        Cell: ({ value }) => {
          const status = statusMap[getStatusValue(value)] || statusMap.default;
          return <Badge color={status.color}>{status.name}</Badge>;
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => {
          const cpt = row.original;
          const rowKey = getRowActionKey(cpt);

          return (
            <div className="relative whitespace-nowrap">
              <button
                type="button"
                aria-label={`Actions for CPT ${cpt.cpt_code || ""}`}
                onClick={(event) => handleActionMenuToggle(cpt, event)}
                className={clsx(
                  "inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold shadow-sm transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                  openActionMenu === rowKey
                    ? "border-blue-400 bg-blue-50 text-blue-700 shadow-blue-100"
                    : "border-gray-300 bg-white text-gray-700 hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                )}
              >
                <FontAwesomeIcon
                  icon={faEllipsisVertical}
                  className="text-gray-500"
                />
                <span>Actions</span>
              </button>
              {openActionMenu === rowKey &&
                actionMenuPosition &&
                typeof document !== "undefined" &&
                createPortal(
                  <>
                    <button
                      type="button"
                      aria-label="Close CPT actions menu"
                      className="fixed inset-0 z-[9998] cursor-default bg-transparent"
                      onClick={closeActionMenu}
                    />
                    <div
                      className="fixed z-[9999] w-72 overflow-visible"
                      style={{
                        top: `${actionMenuPosition.top}px`,
                        left: `${actionMenuPosition.left}px`,
                      }}
                    >
                      <div
                        className={clsx(
                          "absolute right-6 h-3 w-3 rotate-45 border bg-white",
                          actionMenuPosition.placement === "top"
                            ? "-bottom-1.5 border-l-0 border-t-0 border-gray-200"
                            : "-top-1.5 border-b-0 border-r-0 border-gray-200"
                        )}
                      />
                      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl shadow-slate-900/15 ring-1 ring-black/5">
                        <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            CPT Actions
                          </p>
                        </div>
                        <div className="space-y-1 p-2">
                          {getRowActions(cpt).map((action) => (
                            <button
                              key={action.key}
                              type="button"
                              onClick={() => {
                                action.onClick();
                                closeActionMenu();
                              }}
                              className="group flex w-full items-center rounded-lg border border-transparent px-3 py-2.5 text-left text-sm text-gray-700 transition-all duration-150 hover:translate-x-0.5 hover:border-blue-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:text-blue-700 hover:shadow-sm"
                            >
                              <span className="mr-3 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 transition-all duration-150 group-hover:bg-white group-hover:shadow-sm">
                                <action.Icon
                                  className={clsx("h-5 w-5", action.color)}
                                />
                              </span>
                              <span className="truncate font-medium">
                                {action.label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
            </div>
          );
        },
      },
    ],
    [
      actionMenuPosition,
      closeActionMenu,
      handleActionMenuToggle,
      openActionMenu,
    ]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        initialState: { pageIndex: currentPage - 1, pageSize: itemsPerPage },
        manualPagination: true,
        pageCount: Math.ceil(totalCount / itemsPerPage),
      },
      usePagination
    );

  const handleSort = (column) => {
    if (!column || !column.id || column.disableSortBy) return;
    if (sortBy === column.id) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column.id);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/cpt/export`, {
        headers: getAuthHeaders(),
        responseType: "blob",
        params: {
          search_string: searchQuery,
          status_flag: statusFilter === "ALL" ? undefined : statusFilter,
        },
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "cpt_codes.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CPT codes exported successfully.");
    } catch (error) {
      toast.error("Failed to export CPT codes.");
    } finally {
      setExportLoading(false);
    }
  };

  const openStatusModal = (cpt) => {
    const currentStatus = getStatusValue(cpt?.active_flag);
    const nextStatus =
      statusOptions.find((status) => status.value !== currentStatus)?.value || "1";

    setSelectedCpt(cpt);
    setNewStatus(nextStatus);
    setRemarks("");
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setStatusModalOpen(false);
    setSelectedCpt(null);
    setRemarks("");
  };

  const handleSubmitStatus = async () => {
    if (!selectedCpt) return;
    if (newStatus === getStatusValue(selectedCpt.active_flag)) {
      toast.error("Selected status is already applied to this CPT. Please select a different status.");
      return;
    }
    if (newStatus === "2" && !remarks.trim()) {
      toast.error("Remarks is required when terminating/cancelling.");
      return;
    }
    setSavingStatus(true);
    try {
      await axios.post(
        `${BASE_URL}/cpt/${selectedCpt.cpt_code}/status`,
        {
          new_active_flag: newStatus,
          remarks,
        },
        { headers: getAuthHeaders() }
      );
      // Optimistically update the data state to reflect the new status
      setData((prev) =>
        prev.map((item) =>
          item.cpt_code === selectedCpt.cpt_code
            ? { ...item, active_flag: Number(newStatus) }
            : item
        )
      );
      toast.success("Status updated");
      closeStatusModal();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const isSelectedStatusCurrent = selectedCpt
    ? newStatus === getStatusValue(selectedCpt.active_flag)
    : false;

  return (
    <div className="mx-auto w-full max-w-1xl p-4">
      <ToastContainer />
      <LoadingModal isOpen={exportLoading} />
      {/* Toolbar */}
      <div className="mb-4 rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-white/10 p-2 ring-1 ring-white/20 backdrop-blur">
              <FontAwesomeIcon icon={faFileMedical} className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold">Current Procedural Terminology (CPT Rates)</h1>
              <div className="text-xs text-white/90">
                {totalCount.toLocaleString()} total record{totalCount === 1 ? "" : "s"}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate("/cpt-batch-upload")}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/20 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faFileUpload} />
              <span>Batch Upload</span>
            </button>
            <button
              onClick={() => navigate("/cpt/add-new-cpt")}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/20 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Add New CPT Rate</span>
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 rounded-md bg-white/10 px-3 py-2 text-sm font-medium ring-1 ring-white/20 hover:bg-white/20"
            >
              <ArrowDownTrayIcon className="h-5 w-5" />
              <span>Export CPT Rate Data</span>
            </button>
          </div>
        </div>

        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search CPT codes..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-md border border-gray-200 bg-white pl-9 pr-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="rounded-md border border-gray-200 px-2 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            <option value="ALL">All Statuses</option>
            <option value="1">Active</option>
            <option value="2">Terminated/Cancelled</option>
            <option value="3">Suspended</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="max-h-[60vh] overflow-auto">
          {loading ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        onClick={() => handleSort(column)}
                        className={clsx(
                          "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                          "cursor-pointer select-none"
                        )}
                      >
                        <div className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.id === sortBy && (
                            <span className="text-gray-400">
                              {sortDirection === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {[...Array(itemsPerPage)].map((_, idx) => (
                  <tr key={idx} className="animate-pulse border-t">
                    {[...Array(columns.length)].map((__, i) => (
                      <td key={i} className="px-4 py-3">
                        <div className="h-4 bg-gray-200 rounded" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : data.length === 0 ? (
            <div className="p-8">
              <EmptyState title="No CPT codes found" subtitle="Try changing your filters or search." />
            </div>
          ) : (
            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
              <thead className="sticky top-0 bg-gray-50">
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        key={column.id}
                        {...column.getHeaderProps()}
                        onClick={() => handleSort(column)}
                        className={clsx(
                          "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600",
                          "cursor-pointer select-none"
                        )}
                      >
                        <div className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.id === sortBy && (
                            <span className="text-gray-400">
                              {sortDirection === "asc" ? "▲" : "▼"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} className="hover:bg-gray-50 transition-colors">
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} className="px-4 py-3 text-sm text-gray-800">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-100 p-3 text-sm text-gray-700 sm:flex-row">
        <div className="text-xs text-gray-600">
          Showing {totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount.toLocaleString()} entries
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" />
            First
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === 1 ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            <ChevronLeftIcon className="h-5 w-5" />
            Previous
          </button>
          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={clsx(
                "rounded-md px-2 py-1 text-xs",
                currentPage === page ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Next
            <ChevronRightIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs",
              currentPage === totalPages ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            )}
          >
            Last
            <ChevronDoubleRightIcon className="h-5 w-5" />
          </button>

          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="ml-1 rounded-md border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
          >
            {[10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size} per page
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Status Modal */}
      <AnimatePresence>
        {statusModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={closeStatusModal}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
              className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 px-4 py-3 text-white">
                <h2 className="text-base font-semibold">
                  Update CPT Status for {selectedCpt?.cpt_code}
                </h2>
                <div className="text-xs text-white/80 truncate">{selectedCpt?.cpt_desc}</div>
              </div>
              <div className="p-4 space-y-4">
                <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  Current Status:{" "}
                  <span className="font-medium">
                    {getStatusLabel(selectedCpt?.active_flag)}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">New Status *</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  >
                    {statusOptions.map((status) => {
                      const isCurrent =
                        status.value === getStatusValue(selectedCpt?.active_flag);

                      return (
                        <option
                          key={status.value}
                          value={status.value}
                          disabled={isCurrent}
                        >
                          {status.label}
                          {isCurrent ? " (Current)" : ""}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Remarks {newStatus === "2" ? "*" : "(optional)"}
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={4}
                    placeholder="Enter reason for status change..."
                    className="mt-1 block w-full rounded-md border border-gray-200 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeStatusModal}
                    disabled={savingStatus}
                    className="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-800 hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitStatus}
                    disabled={savingStatus || isSelectedStatusCurrent}
                    className={clsx(
                      "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white",
                      savingStatus || isSelectedStatusCurrent
                        ? "cursor-not-allowed bg-blue-300"
                        : "bg-blue-600 hover:bg-blue-700"
                    )}
                  >
                    {savingStatus ? (
                      <>
                        <svg
                          className="h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Saving…
                      </>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CptTable;
